import { palette, fontFamily } from '../../../styles/tokens';
import type { Message } from '../types';

interface Props {
  message: Message;
  showTranslations: boolean;
  preferredLang?: string;
  isLastSent?: boolean;
  isConsecutive?: boolean;
  showSender?: boolean;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit' });
}

export function MessageBubble({ message: m, showTranslations, preferredLang = 'en', isLastSent, isConsecutive, showSender }: Props) {
  const sent = m.isSent;
  const translated = showTranslations && m.translations && m.translations[preferredLang];
  const displayText = translated || m.content;
  // Group threads: label incoming messages with the sender's name (first of a run).
  const senderLabel = showSender && !sent && !isConsecutive && m.senderName && m.senderName !== 'Unknown'
    ? m.senderName
    : null;

  return (
    <div style={{ marginTop: isConsecutive ? 2 : 8 }}>
      {senderLabel && (
        <div style={{
          fontSize: 11, fontWeight: 600, color: palette.g500, fontFamily,
          padding: '0 16px', marginBottom: 3, marginLeft: 2,
        }}>
          {senderLabel}
        </div>
      )}
      <div style={{
        display: 'flex', justifyContent: sent ? 'flex-end' : 'flex-start',
        padding: '0 16px',
      }}>
        <div style={{
          maxWidth: '65%', padding: '10px 14px',
          borderRadius: sent ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
          background: sent ? '#0F6CBD' : '#F0F0F0',
          boxShadow: sent
            ? '0 2px 8px rgba(15, 108, 189, 0.15)'
            : '0 1px 4px rgba(0,0,0,0.06)',
        }}>
          {/* Reference card */}
          {m.reference_type && m.reference_snapshot && (
            <ReferenceCard type={m.reference_type} snapshot={m.reference_snapshot} sent={sent} />
          )}

          {/* Message text */}
          <div style={{
            fontSize: 14, lineHeight: 1.45, color: sent ? '#fff' : palette.g800,
            fontFamily, wordBreak: 'break-word', whiteSpace: 'pre-wrap',
          }}>
            {displayText}
          </div>

          {/* Translation label */}
          {translated && m.original_language && (
            <div style={{
              fontSize: 10.5, color: sent ? 'rgba(255,255,255,0.55)' : '#8E8E93',
              marginTop: 4, fontStyle: 'italic',
            }}>
              Translated from {m.original_language}
            </div>
          )}

          {/* Timestamp */}
          <div style={{
            fontSize: 11, color: sent ? 'rgba(255,255,255,0.55)' : '#8E8E93',
            marginTop: 3, textAlign: 'right',
          }}>
            {formatTime(m.created_at)}
          </div>
        </div>
      </div>

      {/* Read receipt — last sent message only */}
      {sent && isLastSent && (
        <div style={{
          textAlign: 'right', paddingRight: 18, marginTop: 2,
          fontSize: 11, color: '#8E8E93', fontFamily,
        }}>
          {m.read_at ? 'Read' : 'Delivered'}
        </div>
      )}
    </div>
  );
}

// ── Inline reference card ──

function ReferenceCard({ type, snapshot, sent }: {
  type: string; snapshot: Record<string, unknown>; sent: boolean;
}) {
  const s = snapshot;
  const title = (s.title as string) ?? type;
  const status = (s.status as string) ?? '';
  const amount = s.amount != null ? `$${s.amount}` : '';
  const bg = sent ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.04)';
  const textColor = sent ? '#fff' : palette.g800;
  const mutedColor = sent ? 'rgba(255,255,255,0.7)' : '#8E8E93';

  return (
    <div style={{ background: bg, borderRadius: 10, padding: '8px 12px', marginBottom: 6 }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: mutedColor, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
        {type}
      </div>
      <div style={{ fontSize: 13, fontWeight: 500, color: textColor }}>{title}</div>
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        {status && <span style={{ fontSize: 11, color: mutedColor, textTransform: 'capitalize' }}>{status}</span>}
        {amount && <span style={{ fontSize: 11, color: mutedColor }}>{amount}</span>}
      </div>
    </div>
  );
}
